import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isFunction from 'lodash/isFunction'
import Actions from 'actions'

/**
 * Custom hook to handle React Native WebView messaging
 * @param {boolean} options.sendLoadedMessage - Whether to send 'loaded' message on mount
 * @param {boolean} options.shouldSendLoaded - Condition to determine if loaded message should be sent (e.g., hideBack param)
 * @param {string} options.initialMessage - Custom message to send on mount (optional, defaults to 'loaded')
 * @param {function} options.onMessage - Custom message handler (optional, overrides default behavior)
 * @returns {function} sendMessage - Function to send messages to native app
 * @returns {boolean} isWebView - True if running in React Native WebView environment
 * @returns {boolean} isWebViewAuthenticated - True if in WebView AND has received token from native app
 */
export const useNativeMessage = ({
  sendLoadedMessage = false,
  shouldSendLoaded = true,
  initialMessage = 'loaded',
  onMessage: customMessageHandler = null
} = {}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    // Send message to the native app that the page is loaded
    // This message enables/controls native navigation back on this page
    if (
      isFunction(window.ReactNativeWebView?.postMessage) &&
      sendLoadedMessage &&
      shouldSendLoaded
    ) {
      window.ReactNativeWebView.postMessage(initialMessage)
    }

    // Default message handler
    const defaultProvidersMessageHandler = event => {
      let data = event.data
      try {
        const parsedData = JSON.parse(data)
        data = parsedData
      } catch {
        // Do nothing - data remains as is
      }

      // Handle 'take_my_token_pcp' message type
      if (data?.type === 'take_my_token_pcp') {
        dispatch(
          Actions.createAction(Actions.PROVIDER_AUTH_RECEIVED, {
            token: data?.token,
            telehealthPcp: data?.telehealthPcp,
            physicalPcp: data?.physicalPcp,
            isChangeTelehealthPcpAvailbale:
              data?.isChangeTelehealthPcpAvailbale,
            memberCopay: data?.memberCopay
          })
        )
      }
    }

    // Use custom handler if provided, otherwise use default
    const messageHandler =
      customMessageHandler || defaultProvidersMessageHandler

    // Add event listeners for both Android (document) and iOS (window)
    window.addEventListener('message', messageHandler, false)
    document.addEventListener('message', messageHandler, false)

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('message', messageHandler, false)
      document.removeEventListener('message', messageHandler, false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check if we're running in a React Native WebView (environment check)
  const isWebView = isFunction(window.ReactNativeWebView?.postMessage)

  // Check if we have received authentication token from native app
  const providerToken = useSelector(state => state.providers?.providerToken)

  // Combined check: Are we in WebView AND have we received the token?
  // This is more certain than just checking the WebView environment
  const isWebViewAuthenticated = isWebView && !!providerToken

  // Helper function to send messages to native app
  const sendMessage = message => {
    if (isWebView) {
      const messageToSend =
        typeof message === 'string' ? message : JSON.stringify(message)
      window.ReactNativeWebView.postMessage(messageToSend)
    }
  }

  return { sendMessage, isWebView, isWebViewAuthenticated }
}

export default useNativeMessage
